import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import type { FieldValues, UseFormReturn } from 'react-hook-form'

import { ConfirmDialog } from '@/shared/ui'

/** What to ask before a save: the question, what will happen, and the action's label. */
export interface SaveQuestion {
  readonly title: string
  readonly body: ReactNode
  readonly confirmLabel: string
  /** `error` for one that removes or ends something; `primary` otherwise, as a send. */
  readonly tone?: 'error' | 'primary'
}

/**
 * Asks before the `RecordForm` inside it saves, when `question` gives one for the form's values,
 * such as a bulk edit that deactivates people or a message sent to a class: the save goes ahead
 * only once confirmed. A form with fields to fix shows them at once, without asking.
 */
export function ConfirmBeforeSave<V extends FieldValues, O>(props: {
  readonly form: UseFormReturn<V, unknown, O>
  readonly question: (values: V) => SaveQuestion | null
  readonly children: ReactNode
}) {
  const { form, question } = props
  // The form whose save waits on the question; the question stays while the dialog fades out.
  const [asking, setAsking] = useState<HTMLFormElement | null>(null)
  const [shown, setShown] = useState<SaveQuestion | null>(null)
  // The form to save once the question has closed and given focus back.
  const confirmed = useRef<HTMLFormElement | null>(null)
  // True while a submit this component lets through is dispatched.
  const passing = useRef(false)

  const pass = useCallback((target: HTMLFormElement) => {
    passing.current = true
    try {
      target.requestSubmit()
    } finally {
      passing.current = false
    }
  }, [])

  const intercept = useCallback(
    (event: FormEvent<HTMLDivElement>) => {
      const target = event.target
      // A chooser's search form in a dialog reaches here through its portal; only this form's own submit is asked about.
      if (passing.current || !(target instanceof HTMLFormElement) || !event.currentTarget.contains(target)) {
        return
      }
      const asked = question(form.getValues())
      if (asked === null) {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      const ask = async () => {
        if (await form.trigger()) {
          setShown(asked)
          setAsking(target)
        } else {
          pass(target)
        }
      }
      void ask()
    },
    [form, question, pass],
  )

  const confirm = useCallback(() => {
    confirmed.current = asking
    setAsking(null)
  }, [asking])
  const cancel = useCallback(() => setAsking(null), [])

  // Saves once the question has closed, so the form keeps the focus it gave back, as on the Save button.
  useEffect(() => {
    const target = confirmed.current
    if (asking === null && target !== null) {
      confirmed.current = null
      pass(target)
    }
  }, [asking, pass])

  return (
    // Sees a submit before the form does; `contents` keeps it out of the layout.
    <div className="contents" onSubmitCapture={intercept}>
      {props.children}
      <ConfirmDialog
        open={asking !== null}
        title={shown?.title ?? ''}
        confirmLabel={shown?.confirmLabel ?? ''}
        tone={shown?.tone ?? 'error'}
        onConfirm={confirm}
        onClose={cancel}
      >
        {shown?.body}
      </ConfirmDialog>
    </div>
  )
}
